import attachmentEngine from '../AttachmentEngine';
import config from '../../../../config.json';
import documentService from '../../../../services/DocumentService';
import responseHelper from '../../../../services/ResponseHelper';
import storageService from '../../../../services/StorageService';
import CoreBase from '../../../../services/engines/CoreBase';

const appName = config.appName;
const _name = 'OnPushAllHandler';

/**
 * Handles pushing all files to their assigned storage provider.
 * Iterates sequentially — updates uploadProgress per file via state.
 * On failure: continues to next file, injects failed doc into AttachmentEngine cache.
 * uploadState is always reset to 'idle' in finally — controls never stay locked.
 */
export default class OnPushAllHandler extends CoreBase {
    /**
     * @param {object} state - shared upload state from UploadAttachmentHandler
     */
    constructor(state) {
        super();
        this._state = state;
    }

    /**
     * @param {{ jid: string, onProgress: Function }} data
     * onProgress called after each repaint-worthy state change, including byte-level progress
     */
    async handle({ jid, onProgress }) {
        if (!this._state.files.length) return;
        if (jid === '') return;

        this._state.uploadState = 'uploading';
        const total = this._state.files.length;

        try {
            for (let i = 0; i < total; i++) {
                const item = this._state.files[i];

                this._state.uploadProgress = {
                    fileName: item.fileName,
                    current: i + 1,
                    total,
                    percent: 0,
                };

                // Notify dialog to repaint progress bar at start of file
                onProgress();

                // Enrich item with upload context — item is the request
                item.jid = jid;
                item.rid = jid;
                item.app = appName;

                const resp = await documentService.postDocument(item);
                if (responseHelper.check(resp, '', `${_name}.handle`) < 0) {
                    this.onError(`postDocument error: ${resp?.status}`);
                    item.status = 'failed';
                    continue;
                }

                const uploadUrl = resp.data.data.signedUploadUrl;
                // Signed download URL — used by lightbox without extra round trip
                const downloadUrl = resp.data.data.signedDownloadUrl;

                try {
                    // Upload with byte-level progress — onProgress fires per XHR chunk
                    await storageService.upload(uploadUrl, item.file, ({ percent }) => {
                        this._state.uploadProgress = { ...this._state.uploadProgress, percent };
                        onProgress();
                    });
                } catch (err) {
                    this.onError(`upload error: ${err.message}`);
                    item.status = 'failed';
                    continue;
                }

                item.rk = resp.data.data.did;
                item.url = downloadUrl;
                item.status = 'confirmed';

                // TODO: post status, set to confirmed
                const stateReq = { rk: item.rk, stateId: 2 };
                const patchResult = await documentService.patchDocument(stateReq);

                // Inject completed doc into engine cache — visible in AttachmentStrip immediately
                attachmentEngine.onDocumentsReceived([
                    {
                        rk: item.rk,
                        rid: jid,
                        url: item.url,
                        fileName: item.fileName,
                        mimeType: item.mimeType,
                        description: item.description,
                        rotation: 0,
                        uploadFailed: false,
                    },
                ]);
            }
        } finally {
            // Always unlock controls — even if an unexpected error escapes the loop
            this._state.uploadState = 'idle';
            this._state.uploadProgress = null;
            onProgress();
        }
    }
}
